/**
 * Getting the user's attention.
 *
 * Before this file the app had no way to do that at all: no sound, nothing
 * that touched document.title, no OS notification, and no visibilitychange
 * handling anywhere in the client. A socket event landing in a backgrounded
 * tab sat there silently until the person happened to look. That is merely
 * unhelpful for a chat mention and fatal for a phone call, which is worthless
 * if nobody notices it inside thirty-five seconds.
 *
 * Deliberately dependency-free and framework-free — it is called from a React
 * context today, but nothing here knows that.
 */

/* ------------------------------------------------------------------ *
 * Audio
 * ------------------------------------------------------------------ */

/**
 * Call sounds are real recordings, played through Web Audio.
 *
 * Three of them, served from client/public/sounds:
 *   incoming      — the ringtone for the person being called
 *   ringback      — what the caller hears while the other phone rings
 *   disconnected  — one short beep when a call ends
 *
 * Web Audio rather than <audio> elements: the files are decoded once and then
 * start instantly, a loop has no gap at the seam, and it rides on the same
 * AudioContext that unlockAudio() resumes on the first click — so a tab left
 * open since this morning can still ring.
 *
 * Every sound keeps a synthesised fallback. A file that fails to load (a
 * stale service worker, a proxy stripping /sounds) must not turn into a call
 * that rings in silence.
 */
let audioCtx = null;
let unlocked = false;

const ensureCtx = () => {
    if (!audioCtx) {
        const Ctor = window.AudioContext || window.webkitAudioContext;
        if (!Ctor) return null;
        audioCtx = new Ctor();
    }
    return audioCtx;
};

const resumeCtx = (ctx) => {
    if (ctx && ctx.state === 'suspended') ctx.resume().catch(() => { });
};

const SOUND_FILES = {
    incoming: 'incoming.mp3',
    ringback: 'ringing.mp3',
    disconnected: 'disconnected.mp3'
};

const soundUrl = (name) => `${process.env.PUBLIC_URL || ''}/sounds/${SOUND_FILES[name]}`;

/*
 * Fetching and decoding are cached separately. The bytes can be fetched at
 * page load, before there is an AudioContext; decoding needs one, and creating
 * one before any user gesture only earns a console warning.
 */
const fetched = {};   // name -> Promise<ArrayBuffer | null>
const decoded = {};   // name -> Promise<AudioBuffer | null>

const fetchSound = (name) => {
    if (!fetched[name]) {
        fetched[name] = fetch(soundUrl(name))
            .then((res) => {
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                return res.arrayBuffer();
            })
            .catch((err) => {
                console.warn(`[attention] could not load the ${name} sound:`, err?.message || err);
                delete fetched[name];   // try again next time
                return null;
            });
    }
    return fetched[name];
};

const getBuffer = (name) => {
    const ctx = ensureCtx();
    if (!ctx) return Promise.resolve(null);

    if (!decoded[name]) {
        decoded[name] = fetchSound(name)
            .then((data) => (data
                // Callback form: older Safari never returned a promise here.
                ? new Promise((resolve, reject) => ctx.decodeAudioData(data, resolve, reject))
                : null))
            .catch((err) => {
                console.warn(`[attention] could not decode the ${name} sound:`, err?.message || err);
                return null;
            })
            .then((buffer) => {
                if (!buffer) {
                    // decodeAudioData detaches the bytes, so a retry refetches.
                    delete decoded[name];
                    delete fetched[name];
                }
                return buffer;
            });
    }
    return decoded[name];
};

/** Fetch the call sounds ahead of time, so the first ring is not late. */
const preloadCallSounds = () => {
    for (const name of Object.keys(SOUND_FILES)) fetchSound(name);
};

/**
 * Browsers refuse to play audio on a page the user has never interacted with,
 * and an AudioContext created before any gesture starts in a `suspended`
 * state. Resuming it needs a real user gesture, so one is caught at app mount
 * and used for nothing else.
 *
 * Without this, ringing works perfectly in development — where you have been
 * clicking around the whole time — and fails silently for the user who left a
 * tab open since this morning, which is precisely the user being called.
 */
export const unlockAudio = () => {
    preloadCallSounds();
    if (unlocked) return;

    const unlock = () => {
        const ctx = ensureCtx();
        resumeCtx(ctx);
        unlocked = true;
        // Decode now, while nothing is waiting on it.
        for (const name of Object.keys(SOUND_FILES)) getBuffer(name);
        window.removeEventListener('pointerdown', unlock);
        window.removeEventListener('keydown', unlock);
    };

    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
};

/* --------------------------- synthesis --------------------------- */

/** One tone, ramped at both ends — a square-edged gain change is an audible click. */
const tone = (ctx, at, { freqs, duration, volume }) => {
    const gain = ctx.createGain();
    gain.connect(ctx.destination);

    gain.gain.setValueAtTime(0, at);
    gain.gain.linearRampToValueAtTime(volume, at + 0.03);
    gain.gain.setValueAtTime(volume, at + duration - 0.03);
    gain.gain.linearRampToValueAtTime(0, at + duration);

    for (const freq of freqs) {
        const osc = ctx.createOscillator();
        osc.type = 'sine';
        osc.frequency.value = freq;
        osc.connect(gain);
        osc.start(at);
        osc.stop(at + duration + 0.02);
    }
};

/*
 * The fallback ring: 440 Hz + 480 Hz is the actual North American ring tone.
 * Two bursts per cycle, repeated every three seconds.
 */
const RING_CYCLE_MS = 3000;

const ringCycle = (volume) => {
    const ctx = ensureCtx();
    if (!ctx) return;
    resumeCtx(ctx);

    const now = ctx.currentTime;
    tone(ctx, now, { freqs: [440, 480], duration: 0.4, volume });
    tone(ctx, now + 0.6, { freqs: [440, 480], duration: 0.4, volume });
};

/* ---------------------------- playback ---------------------------- */

/**
 * Loop a sound until stopped.
 *
 * Returns straight away with a handle; the sound starts once it is decoded.
 * Stopping before then simply means it never starts — a call answered in the
 * first 100 ms must not ring afterwards.
 */
const startLoop = (name, volume, fallbackVolume) => {
    const handle = { stopped: false, ctx: null, source: null, gain: null, timer: null };

    const ctx = ensureCtx();
    if (!ctx) return handle;
    resumeCtx(ctx);
    handle.ctx = ctx;

    getBuffer(name).then((buffer) => {
        if (handle.stopped) return;

        if (!buffer) {
            ringCycle(fallbackVolume);
            handle.timer = setInterval(() => ringCycle(fallbackVolume), RING_CYCLE_MS);
            return;
        }

        const gain = ctx.createGain();
        gain.gain.value = volume;
        gain.connect(ctx.destination);

        const source = ctx.createBufferSource();
        source.buffer = buffer;
        source.loop = true;
        source.connect(gain);
        source.start();

        handle.source = source;
        handle.gain = gain;
    });

    return handle;
};

const stopLoop = (handle) => {
    if (!handle || handle.stopped) return;
    handle.stopped = true;

    if (handle.timer) clearInterval(handle.timer);

    if (handle.source && handle.ctx) {
        // Faded over 40 ms rather than cut, which would click.
        const { ctx, source, gain } = handle;
        const now = ctx.currentTime;
        try {
            gain.gain.cancelScheduledValues(now);
            gain.gain.setValueAtTime(gain.gain.value, now);
            gain.gain.linearRampToValueAtTime(0, now + 0.04);
            source.stop(now + 0.05);
        } catch { /* already stopped */ }
        source.onended = () => {
            source.disconnect();
            gain.disconnect();
        };
    }
};

/* ------------------------------------------------------------------ *
 * Which tab rings
 * ------------------------------------------------------------------ */

/**
 * Only one tab may make a sound.
 *
 * The server emits to `user:<id>`, which is every tab this person has open, and
 * it has no way to pick one — so three open tabs would produce three
 * overlapping ringtones a few milliseconds apart. The server does send
 * `call:claimed` once someone answers, but that is a round trip away and the
 * damage is done in the first 200 ms.
 *
 * Election is a deterministic tiebreak rather than a negotiation: every tab
 * announces its id, the lowest id wins, and any tab that hears a lower one
 * falls silent. No timers, no leader heartbeat, nothing to recover if a tab
 * closes mid-call — the next call elects again from scratch.
 *
 * BroadcastChannel is same-origin only, so two different browser profiles both
 * ring. That is what `call:claimed` from the server is for, and why both
 * halves exist.
 */
const TAB_ID = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

let channel = null;
const ensureChannel = () => {
    if (channel === null && typeof BroadcastChannel !== 'undefined') {
        channel = new BroadcastChannel('gts-call');
        channel.onmessage = (event) => {
            const { type, callId, tabId } = event.data || {};
            if (type !== 'ring-claim' || callId !== ringingCallId) return;
            // A lower id than ours is ringing this call. Stop.
            if (tabId < TAB_ID) stopRinging();
        };
    }
    return channel;
};

let ringingCallId = null;
let ringHandle = null;

/**
 * Start ringing for an incoming call, if this tab wins the election.
 *
 * `callId` is also the election key, so two calls arriving at once do not
 * silence each other.
 */
export const startRinging = (callId = 'default') => {
    if (ringingCallId === callId) return;
    stopRinging();

    ringingCallId = callId;

    const ch = ensureChannel();
    if (ch) ch.postMessage({ type: 'ring-claim', callId, tabId: TAB_ID });

    ringHandle = startLoop('incoming', 0.9, 0.16);
};

export const stopRinging = () => {
    stopLoop(ringHandle);
    ringHandle = null;
    ringingCallId = null;
};

/* ------------------------------------------------------------------ *
 * The caller's side
 * ------------------------------------------------------------------ */

/*
 * No election here: only the one tab that placed the call hears it ringing
 * out, because only that tab holds the call.
 */
let ringbackHandle = null;

/** Ringback — the caller hears the other phone ringing. */
export const startRingback = () => {
    if (ringbackHandle) return;
    ringbackHandle = startLoop('ringback', 0.6, 0.08);
};

export const stopRingback = () => {
    stopLoop(ringbackHandle);
    ringbackHandle = null;
};

/** The call is over: the disconnect beep, once. */
export const playDisconnected = () => {
    const ctx = ensureCtx();
    if (!ctx) return;
    resumeCtx(ctx);

    getBuffer('disconnected').then((buffer) => {
        const start = ctx.currentTime + 0.02;

        if (!buffer) {
            tone(ctx, start, { freqs: [425], duration: 0.25, volume: 0.14 });
            return;
        }

        const gain = ctx.createGain();
        gain.gain.value = 0.8;
        gain.connect(ctx.destination);

        const source = ctx.createBufferSource();
        source.buffer = buffer;
        source.connect(gain);
        source.onended = () => gain.disconnect();
        source.start(start);
    });
};

/**
 * A single soft chime — a new message, not a call.
 *
 * `volume` is the peak gain; pass a lower one for something that should be
 * noticed without startling (a screen recording that ended on its own).
 */
export const playPing = (volume = 0.09) => {
    const ctx = ensureCtx();
    if (!ctx) return;
    resumeCtx(ctx);

    const now = ctx.currentTime;
    const gain = ctx.createGain();
    gain.connect(ctx.destination);
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(volume, now + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.5);

    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, now);
    osc.frequency.exponentialRampToValueAtTime(1320, now + 0.08);
    osc.connect(gain);
    osc.start(now);
    osc.stop(now + 0.55);
};

/* ------------------------------------------------------------------ *
 * The tab title
 * ------------------------------------------------------------------ */

/**
 * Flash the browser tab.
 *
 * The one attention signal that survives a muted tab, a denied notification
 * permission and a user who has the window on a second monitor. Nothing else
 * in the app writes document.title, so the original is simply the static one
 * from public/index.html and can be restored verbatim.
 */
let titleTimer = null;
let originalTitle = null;

export const flashTitle = (text) => {
    if (titleTimer) return;
    if (originalTitle === null) originalTitle = document.title;

    let showing = false;
    titleTimer = setInterval(() => {
        showing = !showing;
        document.title = showing ? text : originalTitle;
    }, 900);
};

export const stopFlashTitle = () => {
    if (titleTimer) {
        clearInterval(titleTimer);
        titleTimer = null;
    }
    if (originalTitle !== null) {
        document.title = originalTitle;
        originalTitle = null;
    }
};

/* ------------------------------------------------------------------ *
 * OS notifications
 * ------------------------------------------------------------------ */

/**
 * Ask for notification permission — but only ever from a user action.
 *
 * Never call this on page load. Browsers penalise sites that prompt on arrival
 * (Chrome hides the prompt entirely for users who habitually dismiss them),
 * and a person asked before they know why will decline, permanently. Asked at
 * the moment they place or answer their first call, they say yes.
 */
export const requestNotificationPermission = async () => {
    if (!('Notification' in window)) return 'unsupported';
    if (Notification.permission !== 'default') return Notification.permission;
    try {
        return await Notification.requestPermission();
    } catch {
        return Notification.permission;
    }
};

/**
 * Raise an OS-level banner — only when the tab is actually hidden.
 *
 * A notification for something already on screen is noise, and it is the
 * fastest way to teach someone to turn notifications off.
 */
export const notify = ({ title, body, tag, onClick, force = false }) => {
    if (!('Notification' in window)) return null;
    if (Notification.permission !== 'granted') return null;
    if (!force && !document.hidden) return null;

    try {
        const n = new Notification(title, {
            body,
            tag,
            // Replaces a previous banner with the same tag rather than
            // stacking one per ring cycle.
            renotify: Boolean(tag),
            icon: '/favicon.png'
        });
        n.onclick = () => {
            window.focus();
            if (onClick) onClick();
            n.close();
        };
        return n;
    } catch {
        return null;
    }
};

/** Everything off — one call for "the thing that wanted attention is over". */
export const clearAttention = () => {
    stopRinging();
    stopRingback();
    stopFlashTitle();
};

/* ------------------------------------------------------------------ *
 * One tab per alert
 * ------------------------------------------------------------------ */

/**
 * Claim a one-shot alert for this tab, or find it already taken.
 *
 * The same problem the ring election solves, but for something that cannot be
 * retracted. `startRinging` can afford to start and then fall silent when it
 * hears a lower tab id, because a ringtone is a continuing sound. A single
 * chime is over before the message announcing it arrives, so four open tabs
 * would produce four overlapping pings and four stacked OS banners.
 *
 * localStorage rather than BroadcastChannel, because `setItem` is synchronous
 * and shared: whichever tab is handed the socket event first writes the key and
 * wins, and the rest read it back and stand down. There is no cross-tab CAS in
 * the browser, so a genuine simultaneous write is still possible — that costs
 * a duplicate chime, which is why this is the arbiter for pings and banners and
 * not for anything that has to happen exactly once.
 *
 * Claims expire, so a tab that crashes mid-alert does not silence the others
 * for the rest of the session.
 */
const CLAIMS_KEY = 'gts-alert-claims';
const CLAIM_TTL_MS = 8000;

export const claimAlert = (key) => {
    if (!key) return true;
    let store;
    try {
        store = JSON.parse(localStorage.getItem(CLAIMS_KEY) || '{}');
    } catch {
        store = {};
    }
    // Private browsing and a full quota both throw on write. An alert that
    // cannot be arbitrated is better shown than swallowed.
    if (!store || typeof store !== 'object') store = {};

    const now = Date.now();
    if (store[key] && now - store[key] < CLAIM_TTL_MS) return false;

    for (const [k, at] of Object.entries(store)) {
        if (now - at >= CLAIM_TTL_MS) delete store[k];
    }
    store[key] = now;

    try {
        localStorage.setItem(CLAIMS_KEY, JSON.stringify(store));
    } catch {
        /* unarbitrated; see above */
    }
    return true;
};
